import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Stack, LinearProgress, Chip, Paper, TextField, MenuItem, Select, FormControl, InputLabel, Autocomplete, CircularProgress, Tooltip, IconButton, Divider, Button, TableHead, TableContainer, TableRow, TableCell, TableBody, Card, Table, Grid, Dialog, DialogTitle, DialogContent, Snackbar, Alert } from "@mui/material";
import { CalendarMonth, InfoOutlined, AccessTime, History, ArrowBack, Save, Edit, Add, Visibility, Assignment } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getData, putData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { formatDate } from '../../../utils/dateFormat';
import { getStatusColor, priorityColors } from '../../../utils/statusColor';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../../common/spinner';
import { ClearIcon } from '@mui/x-date-pickers';
import CreateUserStory from './userStoryManagement/createStory';

const errorMessages = {
    reason: 'Reason is missing',
    story: 'Stories incomplete'
}

const EpicDetailsPage = () => {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const orgId = userData?.organization_id;
    const roleBasePath = userRole === 'Manager' ? '/manager' : '/team-lead';
    const { id } = useParams();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({});
    const [projects, setProjects] = useState([]);
    const [productOwners, setProductOwners] = useState([]);
    const [teamLeads, setTeamLeads] = useState([]);
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [dateHistory, setDateHistory] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

    const navigate = useNavigate();

    // useImperativeHandle(ref, () => ({
    //     handleSave
    // }));

    // Calculate Financial Year Boundaries (April to March)
    const financialYearRange = useMemo(() => {
        const today = dayjs();
        const currentMonth = today.month();
        let startYear = today.year();

        if (currentMonth < 3) {
            startYear = today.year() - 1;
        }

        return {
            start: dayjs(`${startYear}-04-01`),
            end: dayjs(`${startYear + 1}-03-31`)
        };
    }, []);

    const fetchDetails = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.fetchEpicById(id));
            if (res.status === "SUCCESS") {
                const data = res.message.epic;
                const history = res.message.date_history || []; // Get history
                setDetails(data);
                setDateHistory(history); // Set history
                setFormData({
                    title: data.title || "",
                    project_id: data.project_id || "",
                    product_owner: data.product_owner_id || "",
                    team_lead: data.team_lead_id || "",
                    description: data.description || "",
                    status: data.status || "Todo",
                    priority: data.priority || "Medium",
                    start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : "",
                    end_date: data.end_date ? dayjs(data.end_date).format('YYYY-MM-DD') : "",
                    progress: data.progress ?? "",
                    change_reason: ""
                });
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchProjects = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectsBySupervisor(userId));
            if (res.status === "SUCCESS") {
                const projectData = res.message.data || res.message || [];
                setProjects(Array.isArray(projectData) ? projectData : []);
            }
        } catch (error) {
            setProjects([]);
            return null;
        }
    }, [userId]);

    const fetchTeamLeads = useCallback(async () => {
        if (!orgId) return;
        setFetchingUsers(true);
        try {
            const res = await getData(API_ENDPOINTS.fetchTeamLeadsForOrganization(orgId));
            if (res.status === "SUCCESS") {
                const leads = res.message.users;
                setTeamLeads(Array.isArray(leads) ? leads : []);
            }
        } catch (error) {
            setTeamLeads([]); // Move this above return
            return null;
        } finally {
            setFetchingUsers(false);
        }
    }, [orgId]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);
    useEffect(() => { fetchProjects(); }, [fetchProjects]);
    useEffect(() => {
        if (isEditing) fetchTeamLeads();
    }, [isEditing, fetchTeamLeads]);

    useEffect(() => {
        const fetchProductOwners = async () => {
            if (!formData.project_id) {
                setProductOwners([]);
                return;
            }
            setFetchingUsers(true);
            try {
                const res = await getData(API_ENDPOINTS.fetchProductOwnersForProject(formData.project_id));
                if (res.status === "SUCCESS") {
                    const owners = res.result;
                    setProductOwners(Array.isArray(owners) ? owners : []);
                }
            } catch (error) {
                setProductOwners([]);
            } finally {
                setFetchingUsers(false);
            }
        };
        fetchProductOwners();
    }, [formData.project_id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'status' && value === 'Completed' ? { progress: 100 } : {})
        }));
    };

    const handleDateChange = (name, newValue) => {
        const dateString = newValue ? newValue.format('YYYY-MM-DD') : "";
        setFormData(prev => {
            const newData = { ...prev, [name]: dateString };
            if (name === 'start_date' && prev.end_date && dayjs(dateString).isAfter(dayjs(prev.end_date))) {
                newData.end_date = "";
            }
            return newData;
        });
    };

    const isDateChanged = useMemo(() => {
        if (!details) return false;

        // Helper to normalize dates to YYYY-MM-DD for strict comparison
        const normalize = (date) => (date ? dayjs(date).format('YYYY-MM-DD') : "");

        const originalStart = normalize(details.start_date);
        const originalEnd = normalize(details.end_date);

        const currentStart = normalize(formData.start_date);
        const currentEnd = normalize(formData.end_date);

        return currentStart !== originalStart || currentEnd !== originalEnd;
    }, [formData.start_date, formData.end_date, details]);

    // Validation Logic
    // const isDescriptionMissing = !formData.description || formData.description.trim() === "";
    const isReasonMissing = isDateChanged && (!formData.change_reason || formData.change_reason.trim() === "");
    const isStoriesIncomplete = formData.status === 'Completed' && !details?.userStories?.every(story => story.status === 'Completed');
    const isSaveDisabled = saving || isReasonMissing || isStoriesIncomplete;

    const handleSave = async () => {
        if (isSaveDisabled) {
            if (isReasonMissing) {
                setSnack({ open: true, message: errorMessages['reason'], severity: 'error' });
            } else if(isStoriesIncomplete){
                setSnack({ open: true, message: errorMessages['story'], severity: 'error' });
            }
            return
        };
        setSaving(true);
        try {
            const payload = {
                title: formData.title,
                project_id: formData.project_id,
                product_owner_id: formData.product_owner,
                team_lead_id: formData.team_lead,
                description: formData.description,
                status: formData.status,
                priority: formData.priority,
                start_date: formData.start_date,
                end_date: formData.end_date,
                progress: formData.progress !== "" ? Number(formData.progress) : undefined,
                reason: isDateChanged ? formData.change_reason : undefined
            };
            const res = await putData(API_ENDPOINTS.updateEpic(id), payload);
            if (res.status === "SUCCESS") {
                await Promise.all([fetchDetails(), fetchProjects(), fetchTeamLeads()]);
                setSnack({ open: true, message: res?.message?.message || 'Epic details updated successfully', severity: 'success' });
                setIsEditing(false)
                // if (onEditSuccess) onEditSuccess();
            }
        } catch (error) {
            setSnack({ open: true, message: error.response?.data?.message || 'Error updating epic details', severity: 'error' })
            return null;
        } finally {
            setSaving(false);
        }
    };

    const getProjectTitle = (id) => projects.find(p => p.id === id)?.project_title || "No project assigned";

    const getTeamMemberDisplayName = (member) => {
        if (!member) return '';
        if (typeof member === 'string') return member;
        if (member.fullName) return member.fullName;
        if (member.first_name && member.last_name) return `${member.first_name} ${member.last_name}`;
        return member.name || member.email || '';
    };

    const activeProjectDates = useMemo(() => {
        const project = projects.find(p => p.id === formData.project_id);
        if (!project) return null;
        return {
            start: dayjs(project.start_date),
            end: dayjs(project.end_date)
        };
    }, [projects, formData.project_id]);

    const today = dayjs().startOf('day');

    useEffect(() => { fetchDetails(); }, [fetchDetails]);
    if (loading) return (
        <Box sx={{ display: 'flex', minHeight: '500px', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={40} sx={{ color: '#6366f1' }} />
        </Box>
    );

    if (!details) return <Box p={4} textAlign="center">No data found.</Box>;

    const statusStyle = getStatusColor(details.status);
    const priorityStyle = priorityColors[details.priority] || priorityColors.Medium;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', minHeight: { xs: 'auto', md: '500px' }, bgcolor: '#fff', flexDirection: 'column' }}>
                {/* Header Section */}
                <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 0 } }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{ color: '#64748b', borderRadius: '0px', p: 1, '&:hover': { bgcolor: '#e0e3e6' } }}
                    >
                        <ArrowBack sx={{ fontSize: 20 }} />
                        <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569', display: {xs: 'none', sm: 'block'} }}>Back</Typography>
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexGrow: 1, py: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Stack sx={{ minWidth: {xs: 140, sm: 0}, flex: 1 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}>{details.title}</Typography>
                            <Typography variant="p" sx={{color: '#6d6d6dff', fontSize: 12}}>ID: #{details.epic_code}</Typography>
                        </Stack>
                        {!isEditing ? <Button variant='contained' size='small' sx={{bgcolor: '#6366f1', textTransform: 'none', height: '30px', flexShrink: 0}} onClick={()=> setIsEditing(true)}>Edit Epic</Button> : (
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={saving ? <Loader size={16} color="inherit" /> : <Save />}
                                    onClick={handleSave}
                                    sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                                >
                                    Save
                                </Button>
                                <Button variant="outlined" size="small" onClick={() => setIsEditing(false)}>Cancel</Button>
                            </Stack>
                        )}
                    </Box>
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1 }}>
                    <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, borderRight: { xs: 'none', md: '1px solid #e2e8f0' }, borderBottom: { xs: '1px solid #e2e8f0', md: 'none' }, overflowY: 'auto' }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '12px', letterSpacing: 0.6, display: 'block', lineHeight: 1.5 }}>Project</Typography>
                                    {isEditing ? (
                                        <Autocomplete
                                            options={projects}
                                            getOptionLabel={(option) => option.project_title || ""}
                                            value={projects.find(p => p.id === formData.project_id) || null}
                                            onChange={(event, newValue) => setFormData(prev => ({ ...prev, project_id: newValue ? newValue.id : "", product_owner: "" }))}
                                            renderInput={(params) => <TextField {...params} size="small" variant="outlined" sx={{ mt: 0.5, bgcolor: '#fff' }} placeholder="Select Project" />}
                                        />
                                    ) : (
                                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 600, color: '#334155' }}>{getProjectTitle(formData.project_id)}</Typography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '12px', letterSpacing: 0.6, display: 'block', lineHeight: 1.5 }}>Product Owner</Typography>
                                    {isEditing ? (
                                        <Autocomplete
                                            options={productOwners}
                                            value={productOwners.find(u => u.id === formData.product_owner) || null}
                                            onChange={(e, value) => setFormData(prev => ({ ...prev, product_owner: value?.id || "" }))}
                                            getOptionLabel={getTeamMemberDisplayName}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            loading={fetchingUsers}
                                            size="small"
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder="Select Product Owner" fullWidth InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (<>{fetchingUsers ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                                                }} sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                            )}
                                        />
                                    ) : (
                                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 600, color: '#334155' }}>{`${details.productOwner.first_name} ${details.productOwner.last_name}` || "No owner assigned."}</Typography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '12px', letterSpacing: 0.6, display: 'block', lineHeight: 1.5 }}>Team Lead</Typography>
                                    {isEditing ? (
                                        <Autocomplete
                                            options={teamLeads}
                                            value={teamLeads.find(u => u.id === formData.team_lead) || null}
                                            onChange={(e, value) => setFormData(prev => ({ ...prev, team_lead: value?.id || "" }))}
                                            getOptionLabel={getTeamMemberDisplayName}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            loading={fetchingUsers}
                                            size="small"
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder="Select Team Lead" fullWidth InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (<>{fetchingUsers ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                                                }} sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                            )}
                                        />
                                    ) : (
                                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 600, color: '#334155' }}>{`${details.teamLead.first_name} ${details.teamLead.last_name}` || "No lead assigned."}</Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '12px', letterSpacing: 0.5, display: 'block', lineHeight: 1.5 }}>
                                Description
                            </Typography>
                            {isEditing ? (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    size="small"
                                    variant="outlined"
                                    // error={isDescriptionMissing}
                                    // helperText={isDescriptionMissing ? "Description is mandatory" : ""}
                                    sx={{ mt: 0.5, bgcolor: '#fff' }}
                                />
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 600, color: '#334155', lineHeight: 1.3 }}>{details.description || "No description provided."}</Typography>
                            )}
                        </Box>

                        {/* MANDATORY REASON FOR CHANGE BOX */}
                        {isEditing && isDateChanged && (
                            <Box>
                                <Typography variant="subtitle2" color="rgb(98 116 142)" fontWeight="700">
                                    Reason for Date Change *
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    name="change_reason"
                                    placeholder="Explain why the timeline has changed..."
                                    value={formData.change_reason}
                                    onChange={handleInputChange}
                                    size="small"
                                    variant="outlined"
                                    error={isReasonMissing}
                                    helperText={isReasonMissing ? "Reason is required when dates are modified" : ""}
                                    sx={{ bgcolor: '#fff', mt: 1 }}
                                />
                            </Box>
                        )}

                        {/* Add this after the "Last Updated" Paper component */}
                        {/* Timeline History Section */}
                        {dateHistory.length > 0 && (
                            <Paper variant="outlined" sx={{ mt: 3, borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <History sx={{ fontSize: 18, color: '#64748b' }} />
                                    <Typography variant="overline" fontWeight="700" sx={{ color: '#475569' }}>Timeline History</Typography>
                                </Box>

                                <Box sx={{ maxHeight: '400px', overflowY: 'auto', p: 2 }}>
                                    <Stack spacing={2}>
                                        {dateHistory.map((item, index) => (
                                            <Box key={index} sx={{ position: 'relative', pl: 2, borderLeft: '2px solid #e2e8f0', pb: 1 }}>
                                                {/* Dot on the timeline */}
                                                <Box sx={{
                                                    position: 'absolute', left: '-6px', top: '4px',
                                                    width: '10px', height: '10px', borderRadius: '50%',
                                                    bgcolor: '#cbd5e1', border: '2px solid #fff'
                                                }} />

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="textSecondary" fontWeight="600" sx={{ display: 'block' }}>
                                                            {dayjs(item.createdAt).format('MMM D, YYYY • h:mm A')}
                                                        </Typography>

                                                        {/* NEW: Person who changed it */}
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                            <Box sx={{
                                                                width: 20, height: 20, borderRadius: '50%',
                                                                bgcolor: '#6366f1', color: '#fff',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: '10px', fontWeight: 'bold'
                                                            }}>
                                                                {item.changedBy?.first_name?.[0] || '?'}
                                                            </Box>
                                                            <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 500 }}>
                                                                {item.changedBy
                                                                    ? `${item.changedBy.first_name} ${item.changedBy.last_name}`
                                                                    : 'System'}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>

                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                                                        <Chip
                                                            size="small"
                                                            label={`Start: ${formatDate(item.modified_start_date)}`}
                                                            sx={{ fontSize: '10px', height: '20px', bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 600 }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`End: ${formatDate(item.modified_end_date)}`}
                                                            sx={{ fontSize: '10px', height: '20px', bgcolor: '#fff1f2', color: '#ef4444', fontWeight: 600 }}
                                                        />
                                                    </Stack>
                                                </Box>

                                                <Typography variant="body2" sx={{
                                                    fontSize: '12px',
                                                    color: '#475569',
                                                    fontStyle: 'italic',
                                                    bgcolor: '#f1f5f9',
                                                    p: 1.2,
                                                    borderRadius: 1.5,
                                                    wordBreak: 'break-word',
                                                    border: '1px dashed #e2e8f0'
                                                }}>
                                                    {item.reason || 'No reason provided'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            </Paper>
                        )}
                        <Card variant='outlined' sx={{marginTop: 2, borderRadius: 3}}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }}}>
                                <Typography variant='h6' sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>User Stories</Typography>
                                <Button
                                    variant='contained'
                                    startIcon={<Add />}
                                    sx={{textTransform: 'none', width: 'auto'}}
                                    onClick={() => setIsStoryModalOpen(true)}
                                >
                                    Add
                                </Button>
                            </Box>
                            {details.userStories && 
                            <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead sx={{backgroundColor:'#f1f1f1ff'}}>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 600}}>User Story Name</TableCell>
                                            <TableCell sx={{fontWeight: 600}}>Points</TableCell>
                                            <TableCell sx={{fontWeight: 600}}>Progress</TableCell>
                                            <TableCell sx={{fontWeight: 600}}>Timeline</TableCell>
                                            <TableCell sx={{fontWeight: 600}}>Priority</TableCell>
                                            <TableCell sx={{fontWeight: 600}}>Status</TableCell>
                                            <TableCell align='center' sx={{fontWeight: 600}}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {details.userStories.map((story) => (
                                            <TableRow key={story.id}>
                                                <TableCell sx={{maxWidth: { xs: 200, sm: 360 }}}>
                                                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1}}>
                                                        <Typography variant='span' sx={{color: '#3b82f6', fontWeight:600}}>{story.story_code}</Typography>
                                                        <Typography variant='span' sx={{fontWeight: 600}}>{story.title}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{fontWeight: 600}}>
                                                        {story.story_points}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction='row' alignItems='center' spacing={1}>
                                                        <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                                            <CircularProgress size={40} sx={{color: '#bdbdbdff'}}  variant='determinate' value={100}/>
                                                            <CircularProgress size={40} sx={{
                                                                color: story.progress >= 100 ? '#22c55e' : '#6366f1',
                                                                position: 'absolute',
                                                                left: 0,
                                                                [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                                                            }} variant='determinate' value={Math.min(story.progress || 0, 100)}/>
                                                            <Box
                                                                sx={{
                                                                    top: 0, left: 0, bottom: 0, right: 0,
                                                                    position: 'absolute',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                                                    {`${Math.round(story.progress || 0)}%`}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{fontSize: '0.75rem'}}>{formatDate( story.start_date)} - {formatDate(story.due_date)}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={story.priority}
                                                        size='small'
                                                        variant='outlined'
                                                        sx={{
                                                            bgcolor: priorityColors[story.priority]?.bg,
                                                            color: priorityColors[story.priority]?.color,
                                                            border: `1px solid ${priorityColors[story.priority]?.border || '#cbd5e1'}`,
                                                            textTransform: 'none',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={story.status}
                                                        size='small'
                                                        variant='outlined'
                                                        sx={{
                                                            bgcolor: getStatusColor(story.status).bg,
                                                            color: getStatusColor(story.status).color,
                                                            border: `1px solid ${getStatusColor(story.status).border || '#cbd5e1'}`,
                                                            textTransform: 'none',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <Tooltip title="View Story" placement="top">
                                                        <IconButton size='small'
                                                            onClick={() => navigate(`${roleBasePath}/user-story-details?storyId=${story.id}`)}
                                                        >
                                                            <Visibility size='small'/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>}
                        </Card>
                    </Box>

                    <Box sx={{ width: { xs: '100%', md: '320px' }, p: { xs: 2, sm: 3 }, bgcolor: '#fcfcfd' }}>
                        <Stack direction="column" spacing={2} sx={{ mb: 3 }}>
                            {isEditing ? (
                                <>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select name="status" value={formData.status} label="Status" onChange={handleInputChange}>
                                            <MenuItem value="Todo">To Do</MenuItem>
                                            <MenuItem value="In Progress">In Progress</MenuItem>
                                            <MenuItem value="On Hold">On Hold</MenuItem>
                                            <MenuItem value="Completed">Completed</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Priority</InputLabel>
                                        <Select name="priority" value={formData.priority} label="Priority" onChange={handleInputChange}>
                                            <MenuItem value="Low">Low</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="High">High</MenuItem>
                                        </Select>
                                    </FormControl>
                                </>
                            ) : (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                                    <Chip
                                        label={details.status || "To Do"}
                                        size="small"
                                        sx={{
                                            flex: 1,
                                            minWidth: '110px',
                                            bgcolor: statusStyle.bg,    // Dynamic background
                                            color: statusStyle.color,   // Dynamic text color
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            fontSize: '0.7rem',
                                            border: `1px solid ${statusStyle.color}40` // Optional: subtle border
                                        }}
                                    />
                                    <Chip
                                        icon={<InfoOutlined style={{ fontSize: 16, color: priorityStyle.color }} />}
                                        label={`Priority: ${details.priority || "Medium"}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            flex: 1,
                                            minWidth: '110px',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            bgcolor: priorityStyle.bg,    // Apply the background color
                                            borderColor: priorityStyle.color,
                                            color: priorityStyle.color
                                        }}
                                    />
                                </Stack>
                            )}
                        </Stack>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 3, border: '1px solid #e2e8f0' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
                                <Typography variant="overline" color="textSecondary" fontWeight="700">Overall Progress</Typography>
                                {isEditing ? (
                                    <TextField
                                        name="progress"
                                        value={formData.progress}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                                                handleInputChange(e);
                                            }
                                        }}
                                        type="number"
                                        size="small"
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                        sx={{ width: 90 }}
                                        InputProps={{
                                            endAdornment: <Typography variant="body2" color="textSecondary">%</Typography>
                                        }}
                                    />
                                ) : (
                                    <Typography variant="h5" fontWeight="800" color="primary">{details.progress}%</Typography>
                                )}
                            </Stack>
                            <LinearProgress variant="determinate" value={Number(isEditing ? (formData.progress || 0) : (details.progress || 0))} sx={{ mt: 1.5, height: 8, borderRadius: 4, bgcolor: '#e2e8f0' }} />
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 3, border: '1px solid #e2e8f0' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" >
                                <Typography variant="overline" color="textSecondary" fontWeight="700">Total Stories</Typography>
                                <Typography variant="h5" fontWeight="800" sx={{ color: '#1C7ED6' }}>{details.total_user_stories}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" >
                                <Typography variant="overline" color="textSecondary" fontWeight="700">Completed</Typography>
                                <Typography variant="h5" fontWeight="800" sx={{ color: '#059669' }}>{details.completed_stories}</Typography>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 3, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="overline" color="textSecondary" fontWeight="700">
                                    Epic Timeline
                                </Typography>
                                {/* Info Icon with Calculation Tooltip */}
                                <Tooltip
                                    title="Allocated hours = (Business days − weekends − holidays) × 8 hrs/day"
                                    arrow
                                    placement="top"
                                >
                                    <InfoOutlined sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
                                </Tooltip>
                            </Stack>

                            <Box sx={{ px: 0.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" color="textSecondary" display="block" fontWeight="600">START DATE</Typography>
                                        {isEditing ? (
                                            <DatePicker
                                                label="Start Date"
                                                value={formData.start_date ? dayjs(formData.start_date) : null}
                                                onChange={(date) => handleDateChange('start_date', date)}
                                                format="DD/MM/YYYY"
                                                // DISABLE previous dates relative to Today (19/04)
                                                minDate={activeProjectDates?.start}
                                                // CANNOT exceed Project End Date
                                                maxDate={activeProjectDates?.end || financialYearRange.end}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        helperText: activeProjectDates ? `Project Start: ${activeProjectDates.start.format('DD/MM/YYYY')}` : ''
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                                <CalendarMonth sx={{ fontSize: 16, color: '#6366f1' }} />
                                                <Typography variant="body2" fontWeight="700" noWrap>
                                                    {details.start_date ? dayjs(details.start_date).format('MMM D, YYYY') : 'N/A'}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'left', sm: isEditing ? 'left' : 'right' } }}>
                                        <Typography variant="caption" color="textSecondary" display="block" fontWeight="600">DUE DATE</Typography>
                                        {isEditing ? (
                                            <DatePicker
                                                value={formData.end_date ? dayjs(formData.end_date) : null}
                                                onChange={(date) => handleDateChange('end_date', date)}
                                                format='DD/MM/YYYY'
                                                // CANNOT be before Epic Start Date
                                                minDate={formData.start_date ? dayjs(formData.start_date) : today}
                                                // CANNOT exceed Project End Date
                                                maxDate={activeProjectDates?.end || financialYearRange.end}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        helperText: activeProjectDates ? `Project End: ${activeProjectDates.end.format('DD/MM/YYYY')}` : ''
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ mt: 0.5 }}>
                                                <Typography variant="body2" fontWeight="700" noWrap>
                                                    {details.end_date ? dayjs(details.end_date).format('MMM D, YYYY') : 'N/A'}
                                                </Typography>
                                                <CalendarMonth sx={{ fontSize: 16, color: '#ef4444' }} />
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>

                                {/* Allocated Hours Display */}
                                <Box sx={{ mt: 2, textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }} >
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                        <AccessTime sx={{ fontSize: 16, color: '#6366f1' }} />
                                        <Typography variant="body2" fontWeight="700" color="textSecondary">
                                            {details.allocated_hours} Allocated Hours
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Box>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="overline" color="textSecondary" fontWeight="700">Last updated</Typography>
                            <Typography variant="body2" color="textSecondary">{details.updatedAt ? new Date(details.updatedAt).toLocaleString() : 'Never'}</Typography>
                        </Paper>
                    </Box>
                </Box>
                <Dialog open={isStoryModalOpen} onClose={() => {
                                setIsStoryModalOpen(false);
                            }} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '12px', p: { xs: 0.5, sm: 1 }, m: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: 'auto' } } }}>
                                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        <Box sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6', p: 1.2, borderRadius: '10px', display: 'flex', mr: 2 }}><Assignment sx={{ fontSize: 22 }} /></Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>Create User Story</Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block' }}>
                                                Epic: <Box component="span" sx={{ color: '#475569', fontWeight: 700 }}>{details?.title}</Box> {details?.id && ` (ID: ${details.id})`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton onClick={() => setIsStoryModalOpen(false)} size="small"><ClearIcon /></IconButton>
                                </DialogTitle>
                                <DialogContent dividers>
                                    <CreateUserStory epicData={{...details, project_title:getProjectTitle(formData.project_id)}} onCancel={() => setIsStoryModalOpen(false)} onSuccess={() => { fetchDetails(); setIsStoryModalOpen(false); }} />
                                </DialogContent>
                            </Dialog>
                            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                                <Alert onClose={() => setSnack(p => ({ ...p, open: false }))} severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
                            </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default EpicDetailsPage;